import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  useMediaQuery
} from '@mui/material';
import {
  Refresh,
  Settings,
  CloudQueue,
  WbSunny,
  Thunderstorm,
  AcUnit,
  Foggy,
  Brightness2,
  Brightness4,
  Brightness5,
  Brightness6,
  Brightness7,
  NightsStay
} from '@mui/icons-material';
import { weatherService } from '../../services/weatherService';
import WeatherSettings from './WeatherSettings';
import { useAuth } from '../../contexts/AuthContext';
import { useWeather } from '../../contexts/WeatherContext';

interface WeatherOverlayProps {
  onWeatherChange?: (condition: string) => void;
}

const WeatherOverlay: React.FC<WeatherOverlayProps> = ({ onWeatherChange }) => {
  const { user } = useAuth();
  const { weather, loading, error, refresh } = useWeather();
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Set user location from profile if available
    if (user?.address) {
      weatherService.setUserLocation(user.address);
    }
  }, [user?.address]);

  useEffect(() => {
    if (weather.condition !== 'Unknown') {
      onWeatherChange?.(weather.condition);
    }
  }, [weather.condition, onWeatherChange]);

  const getWeatherIcon = (condition: string) => {
    const conditionLower = condition.toLowerCase();
    switch (conditionLower) {
      case 'clear':
        return <WbSunny sx={{ fontSize: 20 }} />;
      case 'cloudy':
      case 'clouds':
        return <CloudQueue sx={{ fontSize: 20 }} />;
      case 'rain':
      case 'drizzle':
        return <Thunderstorm sx={{ fontSize: 20 }} />;
      case 'snow':
        return <AcUnit sx={{ fontSize: 20 }} />;
      case 'storm':
      case 'thunderstorm':
        return <Thunderstorm sx={{ fontSize: 20 }} />;
      case 'mist':
      case 'fog':
        return <Foggy sx={{ fontSize: 20 }} />;
      default:
        return <CloudQueue sx={{ fontSize: 20 }} />;
    }
  };

  const getTemperatureColor = (temp: number | null) => {
    if (temp === null) return 'inherit';
    if (temp < 0) return '#2196F3'; // Blue for cold
    if (temp < 10) return '#4CAF50'; // Green for cool
    if (temp < 25) return '#FF9800'; // Orange for warm
    return '#F44336'; // Red for hot
  };

  const isSmallScreen = useMediaQuery('(max-width:600px)');

  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {!isSmallScreen && (
          <Alert severity="warning" sx={{ py: 0, px: 1, fontSize: '0.75rem' }}>
            Weather unavailable
          </Alert>
        )}
        <IconButton size="small" onClick={refresh}>
          <Refresh sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  if (loading && weather.condition === 'Unknown') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        {!isSmallScreen && (
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        )}
      </Box>
    );
  }

  if (weather.condition === 'Unknown') {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isSmallScreen ? 0.5 : 1,
          px: isSmallScreen ? 0.5 : 1,
          py: 0.5,
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          }
        }}
      >
        {/* Weather Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getWeatherIcon(weather.condition)}
        </Box>

        {/* Temperature */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: getTemperatureColor(weather.temperature),
            minWidth: isSmallScreen ? '28px' : '35px',
            fontSize: isSmallScreen ? '0.75rem' : '0.875rem'
          }}
        >
          {weather.temperature}°
        </Typography>

        {/* Weather Description - Only on Desktop */}
        {!isSmallScreen && (
          <Tooltip title={weather.description} arrow>
            <Chip
              label={weather.condition}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.7rem',
                height: 20,
                cursor: 'help',
                color: 'inherit',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '& .MuiChip-label': {
                  px: 1
                }
              }}
            />
          </Tooltip>
        )}

        {/* Action Buttons - Compact on Mobile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title="Refresh Weather">
            <IconButton
              size="small"
              onClick={refresh}
              disabled={loading}
              sx={{ p: 0.25 }}
            >
              <Refresh sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>

          {!isSmallScreen && (
            <Tooltip title="Weather Settings">
              <IconButton
                size="small"
                onClick={() => setShowSettings(true)}
                sx={{ p: 0.25 }}
              >
                <Settings sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Weather Settings Dialog */}
      <WeatherSettings
        open={showSettings}
        onClose={() => setShowSettings(false)}
        onConfigChange={refresh}
      />
    </>
  );
};

export default WeatherOverlay;
